import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import TestimonialsSection from "./components/TestimonialsSection";
import HomeFaqSection from "./components/HomeFaqSection";
import SEOMeta from "@/components/feature/SEOMeta";

/* ────────────────────────────── */
/*  HERO BACKGROUND SLIDES */
/* ────────────────────────────── */

const heroSlides = [
  {
    src: "https://readdy.ai/api/search-image?query=A%20stunning%20modern%20steel-frame%20luxury%20residential%20house%20nestled%20in%20a%20misty%20mountain%20landscape%20in%20Taiwan%2C%20dramatic%20architectural%20photography%20with%20warm%20golden%20interior%20lights%20glowing%20through%20floor-to-ceiling%20glass%20windows%20at%20dusk%2C%20clean%20geometric%20contemporary%20design%20with%20dark%20slate%20roof%20and%20warm%20wood%20paneling%2C%20surrounded%20by%20lush%20green%20hills%20and%20distant%20mountain%20peaks%2C%20soft%20atmospheric%20fog%2C%20premium%20architectural%20visualization%2C%20warm%20amber%20and%20deep%20graphite%20color%20palette%2C%20no%20text%20no%20logos%20no%20people%2C%20highly%20detailed%20professional%20photography&width=1920&height=1080&seq=hero-main&orientation=landscape",
    alt: "速築美宅家高端鋼構山景住宅",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Close-up%20of%20modern%20steel-frame%20house%20construction%20process%20showing%20prefabricated%20steel%20beams%20being%20precisely%20assembled%20with%20professional%20equipment%2C%20warm%20industrial%20lighting%20creating%20dramatic%20shadows%20on%20structural%20metal%20framework%2C%20sparks%20from%20welding%20visible%2C%20organized%20construction%20site%20with%20safety%20equipment%2C%20warm%20amber%20and%20steel%20gray%20color%20tones%2C%20professional%20documentary%20construction%20photography%2C%20no%20text%20no%20logos%2C%20highly%20detailed&width=1920&height=1080&seq=hero-slide2-new&orientation=landscape",
    alt: "速築美宅家預製工法施工現場",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Stunning%20modern%20steel-frame%20residential%20house%20exterior%20at%20night%20with%20warm%20amber%20interior%20lighting%20glowing%20through%20large%20floor-to-ceiling%20windows%2C%20contemporary%20geometric%20architecture%20with%20clean%20lines%2C%20landscaped%20garden%20with%20subtle%20pathway%20lighting%2C%20deep%20twilight%20blue%20sky%20with%20warm%20house%20illumination%2C%20professional%20architectural%20night%20photography%2C%20warm%20and%20inviting%20atmosphere%2C%20cream%20and%20charcoal%20tones%20with%20golden%20light%20accents%2C%20no%20text%20no%20logos%20no%20people&width=1920&height=1080&seq=hero-slide3-new&orientation=landscape",
    alt: "速築美宅家夜景住宅外觀",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Modern%20steel-frame%20luxury%20house%20exterior%20detail%20showing%20geometric%20facade%20with%20warm%20wood%20and%20dark%20metal%20structural%20elements%2C%20lush%20tropical%20garden%20with%20flowering%20plants%20in%20foreground%2C%20warm%20golden%20afternoon%20sunlight%20casting%20long%20shadows%20on%20clean%20white%20walls%2C%20close-up%20architectural%20perspective%20from%20garden%20walkway%2C%20premium%20residential%20architecture%20photography%2C%20earth%20tones%20with%20warm%20amber%20and%20cream%20accents%2C%20no%20text%20no%20logos%20no%20people%2C%20highly%20detailed%20editorial%20quality&width=1920&height=1080&seq=hero-slide4-new&orientation=landscape",
    alt: "速築美宅家現代鋼構外觀細節",
  },
];

/* ────────────────────────────── */
/*  STATIC IMAGES */
/* ────────────────────────────── */

const whyImg1 =
  "https://readdy.ai/api/search-image?query=Modern%20steel-frame%20house%20construction%20site%20with%20prefabricated%20steel%20beams%20being%20precisely%20assembled%20by%20professional%20workers%2C%20warm%20afternoon%20sunlight%2C%20industrial%20crane%20and%20organized%20construction%20materials%2C%20clean%20and%20efficient%20building%20process%2C%20professional%20architectural%20construction%20photography%2C%20warm%20golden%20tones%2C%20no%20text%20no%20logos%2C%20premium%20quality%20construction%20visualization&width=600&height=450&seq=why-speed&orientation=squarish";

const whyImg2 =
  "https://readdy.ai/api/search-image?query=Luxurious%20modern%20living%20room%20interior%20with%20floor-to-ceiling%20windows%20showing%20a%20cool%20shaded%20garden%20view%2C%20comfortable%20minimalist%20furniture%2C%20natural%20light%20creating%20a%20serene%20cool%20atmosphere%2C%20high-performance%20thermal%20insulation%20visible%20in%20wall%20construction%20details%2C%20professional%20interior%20photography%2C%20warm%20cream%20and%20cool%20gray%20tones%2C%20energy-efficient%20home%20design%2C%20no%20text%20no%20people&width=600&height=450&seq=why-energy&orientation=squarish";

const whyImg3 =
  "https://readdy.ai/api/search-image?query=Eco-friendly%20modern%20steel-frame%20house%20with%20solar%20panels%20integrated%20into%20the%20roof%20design%2C%20surrounded%20by%20native%20plants%20and%20sustainable%20landscaping%2C%20contemporary%20green%20architecture%2C%20warm%20sunset%20light%20creating%20golden%20reflections%20on%20clean%20building%20surfaces%2C%20professional%20architectural%20sustainability%20photography%2C%20earth%20tones%20with%20warm%20gold%20accents%2C%20no%20text%20no%20logos%2C%20net-zero%20building%20concept&width=600&height=450&seq=why-green&orientation=squarish";

const portfolioImg1 =
  "https://readdy.ai/api/search-image?query=A%20completed%20modern%20steel-frame%20luxury%20house%20on%20a%20private%20plot%20of%20land%20with%20manicured%20garden%2C%20contemporary%20Taiwanese%20residential%20architecture%20with%20clean%20white%20walls%20and%20warm%20wood%20accents%2C%20large%20glass%20windows%20reflecting%20blue%20sky%2C%20professional%20real%20estate%20architectural%20photography%2C%20warm%20afternoon%20light%2C%20premium%20residential%20property%2C%20no%20text%20no%20people%2C%20elegant%20and%20refined&width=700&height=480&seq=portfolio-1&orientation=landscape";

const portfolioImg2 =
  "https://readdy.ai/api/search-image?query=A%20spacious%20multi-generational%20modern%20house%20with%20separate%20living%20wings%20connected%20by%20a%20central%20courtyard%20garden%2C%20contemporary%20steel-frame%20architecture%20with%20warm%20wood%20and%20white%20finishes%2C%20lush%20green%20landscaping%2C%20designed%20for%20family%20living%20with%20both%20privacy%20and%20togetherness%2C%20professional%20architectural%20photography%2C%20warm%20natural%20lighting%2C%20no%20text%20no%20people&width=700&height=480&seq=portfolio-2&orientation=landscape";

const portfolioImg3 =
  "https://readdy.ai/api/search-image?query=A%20newly%20rebuilt%20modern%20urban%20residence%20replacing%20an%20old%20building%2C%20contemporary%20steel-frame%20architecture%20with%20sleek%20glass%20facade%20and%20warm%20interior%20lighting%2C%20surrounded%20by%20established%20trees%20on%20a%20city%20street%2C%20professional%20architectural%20transformation%20photography%2C%20golden%20hour%20warm%20light%2C%20premium%20urban%20renewal%20project%2C%20no%20text%20no%20people&width=700&height=480&seq=portfolio-3&orientation=landscape";

const seminarImg =
  "https://readdy.ai/api/search-image?query=Professional%20seminar%20room%20with%20modern%20minimalist%20interior%20design%2C%20warm%20wood%20conference%20table%2C%20floor-to-ceiling%20windows%20with%20soft%20natural%20light%2C%20architectural%20blueprints%20and%20house%20models%20on%20the%20table%2C%20a%20few%20elegant%20chairs%20arranged%20for%20a%20small%20group%20discussion%2C%20warm%20cream%20and%20wood%20tones%2C%20professional%20meeting%20space%20photography%2C%20no%20text%20no%20people%20visible%2C%20premium%20corporate%20atmosphere&width=1200&height=500&seq=seminar-bg&orientation=landscape";

const finalCtaImg =
  "https://readdy.ai/api/search-image?query=Beautiful%20twilight%20scene%20of%20a%20modern%20steel-frame%20luxury%20house%20with%20warm%20interior%20lights%20glowing%20warmly%20through%20large%20windows%2C%20contemporary%20architecture%20with%20clean%20geometric%20lines%2C%20surrounded%20by%20a%20peaceful%20garden%20with%20soft%20landscaping%20lights%2C%20deep%20blue%20evening%20sky%20with%20warm%20amber%20house%20glow%2C%20professional%20architectural%20twilight%20photography%2C%20cozy%20and%20inviting%20residential%20atmosphere%2C%20no%20text%20no%20logos&width=800&height=500&seq=final-cta&orientation=landscape";

/* ────────────────────────────── */
/*  DATA */
/* ────────────────────────────── */

const heroTags = [
  { label: "最快 4 個月完成建築結構", icon: "ri-time-line" },
  { label: "預製工法 × 鋼構住宅", icon: "ri-building-2-line" },
  { label: "自地自建 × 危老重建", icon: "ri-home-4-line" },
  { label: "室內至少低 5°C 的節能舒適體驗", icon: "ri-temp-cold-line" },
];

const marqueeStats = [
  { value: "50%", label: "工期縮短", icon: "ri-timer-flash-line" },
  { value: "4 個月", label: "最快結構完成", icon: "ri-hammer-line" },
  { value: "±2mm", label: "工廠精度", icon: "ri-ruler-line" },
  { value: "40%", label: "碳排降低", icon: "ri-leaf-line" },
  { value: "5°C", label: "室內更涼", icon: "ri-temp-cold-line" },
  { value: "15 年", label: "結構保固", icon: "ri-shield-check-line" },
  { value: "7 據點", label: "全省服務", icon: "ri-map-pin-line" },
  { value: "100+", label: "完工實績", icon: "ri-building-4-line" },
];

const whyCards = [
  {
    title: "極速完工",
    desc: "採用預製工法，縮減約 50% 施工日期，最快紀錄 4 個月完成建築結構。",
    image: whyImg1,
    icon: "ri-flashlight-line",
  },
  {
    title: "節能適居",
    desc: "高效斷熱與住宅系統設計，炎炎夏日室內至少比室外低 5°C，提升居住舒適度。",
    image: whyImg2,
    icon: "ri-shield-check-line",
  },
  {
    title: "淨零永續",
    desc: "低碳工法、節能概念與永續建築思維，協助打造更符合未來趨勢的家。",
    image: whyImg3,
    icon: "ri-plant-line",
  },
];

const problemCards = [
  {
    title: "已有土地，但不知道能不能蓋",
    desc: "協助評估地目、建蔽率、容積率、法規限制與初步可行性。",
    icon: "ri-map-pin-2-line",
  },
  {
    title: "想蓋自己的家，但不知道預算怎麼抓",
    desc: "協助釐清營建費、設計費、室裝費、貸款與預備金。",
    icon: "ri-calculator-line",
  },
  {
    title: "擔心傳統工程拖太久、變更太多",
    desc: "以流程管理、工項拆解與預製工法降低不確定性。",
    icon: "ri-calendar-check-line",
  },
  {
    title: "想要建築外觀、室內與生活風格一次整合",
    desc: "從建築量體、室內動線到軟裝美學整體規劃。",
    icon: "ri-palette-line",
  },
];

const flowSteps = [
  { num: "01", title: "土地與財務評估", icon: "ri-landscape-line" },
  { num: "02", title: "建築設計", icon: "ri-pencil-ruler-2-line" },
  { num: "03", title: "預算試算", icon: "ri-calculator-line" },
  { num: "04", title: "報建法規", icon: "ri-file-list-3-line" },
  { num: "05", title: "預製工法施工", icon: "ri-hammer-line" },
  { num: "06", title: "室內與軟裝", icon: "ri-sofa-line" },
  { num: "07", title: "驗收交屋", icon: "ri-key-2-line" },
];

const portfolioCards = [
  {
    tag: "已有土地・自地自建",
    title: "自地自建夢想宅",
    desc: "從空地到理想宅，一次整合建築與生活機能。",
    image: portfolioImg1,
  },
  {
    tag: "家族土地・多代同堂",
    title: "多代同堂家族宅",
    desc: "讓三代同堂住得近，也住得自在。",
    image: portfolioImg2,
  },
  {
    tag: "危老重建・賣一留一",
    title: "危老重建新案",
    desc: "從老屋重建到資產再配置。",
    image: portfolioImg3,
  },
];

/* ────────────────────────────── */
/*  COMPONENT */
/* ────────────────────────────── */

export default function Home() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isLoaded, setIsLoaded] = useState<boolean[]>(heroSlides.map(() => false));

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleImageLoad = (idx: number) => {
    setIsLoaded((prev) => {
      const next = [...prev];
      next[idx] = true;
      return next;
    });
  };

  const siteUrl = (typeof import.meta !== "undefined" && import.meta.env?.VITE_SITE_URL) || "https://chronosspace.com";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "速築美宅家 Chronos Space",
        "description": "高端鋼構住宅專家，提供自地自建、危老重建、預製工法一站式服務",
        "inLanguage": "zh-TW",
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${siteUrl}/?s={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/#business`,
        "name": "速築美宅家 Chronos Space",
        "alternateName": "Chronos Space",
        "description": "速築美宅家 Chronos Space 以預製工法重新定義自地自建，整合建築設計、營造施工、室內裝修與軟裝美學，縮短50%施工期，最快4個月完成建築結構。",
        "url": siteUrl,
        "telephone": "",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "台北市",
          "addressRegion": "台灣",
          "addressCountry": "TW"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 25.0569,
          "longitude": 121.5623
        },
        "areaServed": ["台北", "新竹", "台中", "台南", "屏東"],
        "serviceType": ["自地自建", "危老重建", "預製工法", "鋼構住宅", "建築設計", "室內裝修"],
        "sameAs": ["https://lin.ee/OpssTIM"],
        "priceRange": "$$$$",
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "18:00"
          }
        ]
      }
    ]
  };

  return (
    <main>
      <SEOMeta
        title="速築美宅家 Chronos Space｜縮短 50% 施工期 打造一站式高效淨零建築"
        description="速築美宅家 Chronos Space 以預製工法重新定義自地自建，整合建築設計、營造施工、室內裝修與軟裝美學，縮短50%施工期，最快4個月完成建築結構。台北、新竹、台中、台南、屏東全省服務。"
        keywords="速築美宅家,Chronos Space,自地自建,危老重建,鋼構住宅,預製工法,建築設計,室內裝修"
        canonicalPath="/"
        jsonLd={jsonLd}
      />

      {/* ═══════════════════════════════════════
          SECTION 1 — HERO (Ken Burns Auto-slideshow)
         ═══════════════════════════════════════ */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden">
        {/* Background Slides with Ken Burns */}
        <div className="absolute inset-0">
          {heroSlides.map((slide, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-[1500ms] ease-in-out ${
                idx === activeSlide ? "opacity-100 z-[1]" : "opacity-0 z-0"
              }`}
            >
              <div
                className={`w-full h-full ${
                  idx === activeSlide ? "animate-kenburns" : ""
                }`}
              >
                <img
                  src={slide.src}
                  alt={slide.alt}
                  className="w-full h-full object-cover object-top"
                  loading={idx === 0 ? "eager" : "lazy"}
                  onLoad={() => handleImageLoad(idx)}
                />
              </div>
            </div>
          ))}
          {/* Dark overlay with subtle gradient variation */}
          <div className="absolute inset-0 bg-gradient-to-br from-graphite/75 via-graphite/50 to-graphite/80 z-[2]" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full container-main px-4 md:px-8 lg:px-12 py-32 md:py-40">
          <div className="max-w-3xl">
            <span className="tag-label mb-6 inline-block">速築之道，研質生活</span>

            <h1 className="text-white mb-6">
              縮短 50% 施工期
              <br />
              打造一站式高效淨零建築
            </h1>

            <p className="text-cream/90 mb-8 max-w-xl">
              速築美宅家 Chronos Space 以預製工法重新定義自地自建，整合建築設計、營造施工、室內裝修與軟裝美學，協助業主更有效率地完成理想住宅。
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-3 mb-10">
              {heroTags.map((tag) => (
                <span
                  key={tag.label}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-cream text-[1.0625rem] font-medium border border-white/20"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${tag.icon} text-champagne`} />
                  </span>
                  {tag.label}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://lin.ee/OpssTIM"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary gap-2 text-[1.0625rem]"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-line-fill" />
                </span>
                加入 LINE 預約基地可行性評估
              </a>
              <Link
                to="/portfolio"
                className="btn-outline border-white/40 text-white hover:bg-white hover:text-graphite gap-2 text-[1.0625rem]"
              >
                查看作品介紹
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          </div>
        </div>

        {/* Slide indicators */}
        <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-10 flex gap-2.5">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActiveSlide(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-500 cursor-pointer ${
                idx === activeSlide
                  ? "bg-champagne w-8"
                  : "bg-white/40 hover:bg-white/60"
              }`}
              aria-label={`切換到第 ${idx + 1} 張背景`}
            />
          ))}
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-40 left-1/2 -translate-x-1/2 z-10 animate-bounce hidden md:block">
          <span className="w-6 h-6 flex items-center justify-center text-cream/50">
            <i className="ri-arrow-down-line text-xl" />
          </span>
        </div>

        {/* Stats Marquee */}
        <div className="absolute bottom-0 left-0 right-0 z-10 bg-graphite/60 backdrop-blur-md border-t border-white/10">
          <div className="py-3 md:py-4 overflow-hidden">
            <div className="flex animate-marquee whitespace-nowrap">
              {[...marqueeStats, ...marqueeStats, ...marqueeStats].map((stat, idx) => (
                <div
                  key={`${stat.label}-${idx}`}
                  className="inline-flex items-center gap-2 px-6 md:px-8"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-champagne">
                    <i className={`${stat.icon} text-sm`} />
                  </span>
                  <span className="text-champagne font-serif font-bold text-base md:text-lg">
                    {stat.value}
                  </span>
                  <span className="text-cream/70 text-sm md:text-base">
                    {stat.label}
                  </span>
                  <span className="text-cream/30 mx-2">|</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 1.5 — QUICK 3 STEPS BAR
         ═══════════════════════════════════════ */}
      <section className="bg-champagne/8 border-b border-warm-gray/20">
        <div className="container-main px-4 md:px-8 lg:px-12 py-8 md:py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                num: "01",
                title: "加入 LINE 評估基地",
                desc: "提供基地位置與需求，專人初步評估可行性",
                icon: "ri-line-fill",
                href: "https://lin.ee/OpssTIM",
              },
              {
                num: "02",
                title: "預約 90 分鐘說明會",
                desc: "了解流程、預算、工法與常見風險",
                icon: "ri-calendar-line",
                href: "/seminars",
              },
              {
                num: "03",
                title: "啟動建築設計與規劃",
                desc: "從概念到施工圖，一站式完成理想住宅",
                icon: "ri-pencil-ruler-2-line",
                href: "/process",
              },
            ].map((step) => (
              <a
                key={step.num}
                href={step.href}
                target={step.href.startsWith("http") ? "_blank" : undefined}
                rel={step.href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="group flex items-start gap-4 bg-white rounded-xl p-5 border border-warm-gray/40 transition-all duration-300 hover:border-champagne hover:-translate-y-1 cursor-pointer"
              >
                <span className="w-12 h-12 shrink-0 flex items-center justify-center rounded-full bg-champagne/10 text-champagne transition-all duration-300 group-hover:bg-champagne group-hover:text-white">
                  <i className={`${step.icon} text-lg`} />
                </span>
                <div className="min-w-0">
                  <span className="text-champagne/50 text-xs font-serif font-bold block mb-1">
                    STEP {step.num}
                  </span>
                  <h4 className="text-graphite font-semibold text-base mb-1 leading-snug group-hover:text-champagne transition-colors">
                    {step.title}
                  </h4>
                  <p className="text-medium-gray text-sm leading-relaxed mb-0">
                    {step.desc}
                  </p>
                </div>
                <span className="w-6 h-6 shrink-0 flex items-center justify-center text-medium-gray/40 group-hover:text-champagne transition-all ml-auto self-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 2 — WHY SELF-BUILD
         ═══════════════════════════════════════ */}
      <section className="bg-cream section-padding">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="tag-label mb-4 inline-block">WHY SELF-BUILD</span>
            <h2 className="text-graphite mb-4">為什麼選擇自地自建？</h2>
            <p className="text-medium-gray max-w-2xl mx-auto">比買成屋更自由，比傳統營造更精準。</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {whyCards.map((card, i) => (
              <div
                key={i}
                className="group bg-white rounded-2xl overflow-hidden border border-warm-gray/50 transition-all duration-500 hover:-translate-y-2"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm text-champagne shadow-sm">
                      <i className={`${card.icon} text-xl`} />
                    </span>
                  </div>
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="text-graphite mb-3">{card.title}</h3>
                  <p className="text-medium-gray">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 3 — PROBLEMS WE SOLVE
         ═══════════════════════════════════════ */}
      <section className="bg-warm-gray section-padding">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="tag-label mb-4 inline-block">PROBLEMS WE SOLVE</span>
            <h2 className="text-graphite">我們能為你解決什麼問題？</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {problemCards.map((card, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 md:p-8 border border-warm-gray/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-champagne/10 text-champagne mb-6">
                  <i className={`${card.icon} text-2xl`} />
                </span>
                <h3 className="text-graphite mb-3">{card.title}</h3>
                <p className="text-medium-gray">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 4 — SERVICE FLOW (7 steps)
         ═══════════════════════════════════════ */}
      <section className="bg-cream section-padding">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="tag-label mb-4 inline-block">ONE-STOP SERVICE</span>
            <h2 className="text-graphite">從土地評估到入住交屋，一站式完成</h2>
          </div>

          {/* Desktop: horizontal flow with arrows */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Connecting line with shimmer on active */}
              <div className="absolute top-[60px] left-[12.5%] right-[12.5%] h-[2px] bg-champagne/20">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-champagne/40 to-transparent animate-shimmer opacity-60" />
              </div>

              <div className="grid grid-cols-7 gap-4">
                {flowSteps.map((step, i) => (
                  <div key={i} className="relative text-center group">
                    {/* Step circle with enhanced hover */}
                    <div className="w-[120px] mx-auto mb-4">
                      <div className="w-16 h-16 mx-auto rounded-full bg-white border-2 border-champagne/30 flex items-center justify-center mb-4 relative z-10 flow-icon-hover group-hover:border-champagne group-hover:bg-champagne/5 cursor-default">
                        <i className={`${step.icon} text-champagne text-xl transition-transform duration-300 group-hover:scale-110`} />
                      </div>
                      <span className="text-champagne/40 font-serif font-bold text-base block mb-1">
                        {step.num}
                      </span>
                      <h3 className="text-graphite text-[1.125rem] font-semibold leading-tight">
                        {step.title}
                      </h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tablet & Mobile: card grid with hover */}
          <div className="lg:hidden grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
            {flowSteps.map((step, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-5 md:p-6 text-center border border-warm-gray/50 transition-all duration-300 hover:-translate-y-1 hover:border-champagne/30 group"
              >
                <span className="text-champagne/30 font-serif font-bold text-2xl block mb-2">
                  {step.num}
                </span>
                <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-champagne/10 text-champagne transition-all duration-300 group-hover:scale-110 group-hover:bg-champagne/20">
                  <i className={`${step.icon} text-lg transition-transform duration-300 group-hover:scale-110`} />
                </span>
                <h3 className="text-graphite text-[1.0625rem] font-semibold">{step.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 5 — PORTFOLIO HIGHLIGHTS
         ═══════════════════════════════════════ */}
      <section className="bg-graphite section-padding">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="tag-label mb-4 inline-block">PORTFOLIO</span>
            <h2 className="text-white">看見夢想宅落地的樣子</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {portfolioCards.map((card, i) => (
              <div
                key={i}
                className="group relative rounded-2xl overflow-hidden aspect-[4/3] md:aspect-[3/4] cursor-pointer"
              >
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-graphite/90 via-graphite/30 to-transparent" />

                <div className="absolute top-4 left-4">
                  <span className="inline-block px-3 py-1 rounded-full bg-champagne/90 text-graphite text-base font-semibold">
                    {card.tag}
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <h3 className="text-white mb-2">{card.title}</h3>
                  <p className="text-cream/80 mb-4">{card.desc}</p>
                  <Link
                    to="/portfolio"
                    className="inline-flex items-center gap-2 text-champagne font-medium hover:gap-3 transition-all"
                  >
                    查看作品
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 6 — TESTIMONIALS
         ═══════════════════════════════════════ */}
      <TestimonialsSection />

      {/* ═══════════════════════════════════════
          SECTION 6.5 — HOME FAQ
         ═══════════════════════════════════════ */}
      <HomeFaqSection />

      {/* ═══════════════════════════════════════
          SECTION 7 — SEMINAR
         ═══════════════════════════════════════ */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={seminarImg}
            alt="自地自建講座說明會"
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-graphite/75" />
        </div>

        <div className="relative z-10 section-padding">
          <div className="container-main px-4 md:px-8 lg:px-12">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              {/* Left */}
              <div className="w-full lg:w-1/2">
                <span className="tag-label mb-6 inline-block">SEMINAR</span>
                <h2 className="text-white mb-6">第一次自地自建，先聽懂流程再開始</h2>
                <p className="text-cream/80">
                  自地自建不是只找設計師畫圖，也不只是找工班施工，而是一個包含土地、法規、預算、設計、施工與時程管理的完整專案。透過說明會，你可以先掌握關鍵流程與常見風險。
                </p>
              </div>

              {/* Right CTAs */}
              <div className="w-full lg:w-1/2 flex flex-col sm:flex-row gap-4 lg:justify-end">
                <Link
                  to="/seminars"
                  className="btn-primary gap-2 text-[1.0625rem]"
                >
                  預約講座 / 說明會
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-calendar-line" />
                  </span>
                </Link>
                <a
                  href="https://lin.ee/OpssTIM"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline border-white/40 text-white hover:bg-white hover:text-graphite gap-2 text-[1.0625rem]"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-line-fill" />
                  </span>
                  加入 LINE 詢問
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 8 — FINAL CTA
         ═══════════════════════════════════════ */}
      <section className="bg-cream section-padding">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left: Image */}
            <div className="w-full lg:w-1/2">
              <img
                src={finalCtaImg}
                alt="夢想宅夜景"
                className="w-full rounded-2xl object-cover aspect-[16/10]"
                loading="lazy"
              />
            </div>

            {/* Right: Content */}
            <div className="w-full lg:w-1/2 text-center lg:text-left">
              <span className="tag-label mb-6 inline-block">START YOUR PROJECT</span>
              <h2 className="text-graphite mb-6">準備好開始規劃你的理想宅了嗎？</h2>
              <p className="text-medium-gray mb-8">
                無論你已有土地、正在評估危老重建，或只是想先了解預算與流程，歡迎先加入 LINE，讓我們協助你釐清第一步。
              </p>
              <a
                href="https://lin.ee/OpssTIM"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary gap-2 text-[1.0625rem]"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-line-fill" />
                </span>
                加入 LINE@ 諮詢
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}